"use client";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { MAX_PAGE_LIMIT } from "../api/theta";
import {
  blockUser,
  getBlocks,
  reportSender,
  unblockUser,
  type Block,
  type DmReportResult,
  type Page,
} from "../api/messages";
import { conversationsKey, meKey } from "./useConversations";
import { conversationKey } from "./useConversation";

const blocksKey = ["v1", "blocks"] as const;

/**
 * Accept and decline live in `useConversation.ts` now: both answer with the
 * CONVERSATION, `can` recomputed (§2.10 #94/#95), so they splice like every
 * other write on it. What is left here is the two things that are about a
 * PERSON rather than a thread — reporting one, and blocking one.
 */
function useAfterBlockOrReport(conversationId?: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: blocksKey });
    queryClient.invalidateQueries({ queryKey: conversationsKey });
    queryClient.invalidateQueries({ queryKey: meKey });
    if (conversationId !== undefined) {
      queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
    }
  };
}

/**
 * File a spam report with the admins of one shared group.
 *
 * No optimistic drop: whether the row leaves the inbox depends on the
 * `blocked`/`declined` flags the server echoes back, so the caller decides
 * after the fact.
 */
export function useReportSender(conversationId: number) {
  const queryClient = useQueryClient();
  const settle = useAfterBlockOrReport(conversationId);
  return useMutation({
    mutationFn: (body: Parameters<typeof reportSender>[1]): Promise<DmReportResult> =>
      reportSender(conversationId, body),
    onSuccess: () => {
      settle();
      // ReportDialog caches ["v1", "report-targets", …] for 60s. Without this,
      // reopening the dialog inside that minute shows the submit form again
      // with a stale `alreadyReported: false`, and submitting hits the
      // server's duplicate guard as a red error instead of the idempotent
      // "Anmeldt ✓" state the UI already has. The partial key matches every
      // target variant for this conversation.
      queryClient.invalidateQueries({
        queryKey: ["v1", "report-targets", conversationId],
      });
    },
  });
}

/**
 * People the current user has blocked. Only ever shown on the inbox.
 *
 * Cursor-paged (D4): the old endpoint returned every row, and §1.3 forbids a
 * total, so the list says how many it is SHOWING rather than claiming a count
 * it has not been told.
 */
export function useBlockedUsers() {
  return useInfiniteQuery({
    queryKey: blocksKey,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => getBlocks({ cursor: pageParam }),
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : undefined),
    staleTime: 60 * 1000,
    // Hoisted so its identity is stable across renders — an inline `select`
    // is recomputed on every one.
    select: flattenBlocks,
  });
}

/**
 * A bound on the walk below, so a pathological blocklist cannot turn one menu
 * open into an unbounded request loop. At the route's maximum this is 20,000
 * people; the honest answer past it is "not on the pages we read".
 */
const MAX_BLOCK_PAGES = 200;

/**
 * Have I blocked THIS person?
 *
 * A separate query rather than a `.some()` over `useBlockedUsers`, and the
 * difference is not cosmetic: that one is the inbox's "Blokerede personer"
 * list, a `useInfiniteQuery` whose first page is 20 rows behind a "Vis flere"
 * button. Deriving a yes/no from it answers NO for everyone past row 20 — so a
 * moderator who blocked forty spammers saw "Bloker bruger" on the ones they
 * blocked first, and had no way to lift the block from inside the
 * conversation at all.
 *
 * `Conversation` carries no `blocked` flag, so the derivation is unavoidable;
 * what is avoidable is deriving it from one page. This walks `/me/blocks` at
 * the route's maximum until it finds the id or runs out — one request for any
 * blocklist a person actually keeps — and stops the moment it has an answer.
 */
export function useIsBlocked(userId: number | undefined, enabled = true) {
  return useQuery({
    // Under `blocksKey`, so blocking or unblocking anybody invalidates it
    // along with the list.
    queryKey: [...blocksKey, "is-blocked", userId ?? null] as const,
    queryFn: async () => {
      let cursor: string | null = null;
      for (let i = 0; i < MAX_BLOCK_PAGES; i++) {
        const page: Page<Block> = await getBlocks({
          cursor,
          limit: MAX_PAGE_LIMIT,
        });
        if (page.items.some((b) => b.user.id === userId)) return true;
        if (!page.hasMore || !page.nextCursor) return false;
        cursor = page.nextCursor;
      }
      return false;
    },
    enabled: enabled && userId !== undefined,
    staleTime: 60 * 1000,
  });
}

function flattenBlocks(data: InfiniteData<Page<Block>>) {
  return {
    blocked: data.pages.flatMap((page) => page.items),
    hasMore: data.pages[data.pages.length - 1]?.hasMore ?? false,
  };
}

/**
 * Block a person, by id. The 1:1-only restriction is gone with the
 * conversation-scoped route: the block is on a PERSON. It also declines every
 * live 1:1 thread with them, which is why the inbox is invalidated too.
 */
export function useBlockUser(conversationId?: number) {
  const settle = useAfterBlockOrReport(conversationId);
  return useMutation({
    mutationFn: (userId: number) => blockUser(userId),
    onSuccess: settle,
  });
}

/**
 * Lift a block. Note this does NOT undo a decline — conversations declined
 * while the block was active stay gone.
 */
export function useUnblockUser() {
  const settle = useAfterBlockOrReport();
  return useMutation({
    mutationFn: (userId: number) => unblockUser(userId),
    onSuccess: settle,
  });
}
